import { palette } from "./palette"

const text = {
  default: `var(--text-default, ${palette.silicon95})`,
  secondary: `var(--text-secondary, ${palette.silicon60})`,
  highlight: `var(--text-highlight, ${palette.lilac65})`,
  info: `var(--text-info, ${palette.magnetic70})`,
  success: `var(--text-success, ${palette.mint80})`,
  warning: `var(--text-warning, ${palette.turmeric90})`,
  error: `var(--text-error, ${palette.salmon80})`,
  inverted: `var(--text-inverted, ${palette.white})`,
  disabled: `var(--text-disabled, ${palette.silicon40})`,
  placeholder: `var(--text-placeholder, ${palette.silicon45})`,
} as const

const background = {
  default: `var(--background-default, ${palette.white})`,
  groupedPrimary: `var(--background-grouped-primary, ${palette.silicon00})`,
  groupedSecondary: `var(--background-grouped-secondary, ${palette.silicon05})`,
  success: `var(--background-success, ${palette.mint05})`,
  warning: `var(--background-warning, ${palette.turmeric05})`,
  error: `var(--background-error, ${palette.salmon00})`,
  highlight: `var(--background-highlight, ${palette.lilac00})`,
  info: `var(--background-info, ${palette.magnetic05})`,
  inverted: `var(--background-inverted, ${palette.magnetic80})`,
  placeholder: `var(--background-placeholder, ${palette.silicon25})`,
} as const

const border = {
  divider: `var(--border-divider, ${palette.silicon15})`,
  success: `var(--border-success, ${palette.mint50})`,
  warning: `var(--border-warning, ${palette.turmeric50})`,
  error: `var(--border-error, ${palette.salmon40})`,
  focusForm: `var(--border-focus, ${palette.silicon95})`,
  focus: `var(--border-focus, ${palette.lilac65})`,
  action: `var(--border-action, ${palette.lilac65})`,
  info: `var(--border-info, ${palette.magnetic50})`,
  form: `var(--border-form, ${palette.silicon40})`,
  interactive: `var(--border-interactive, ${palette.silicon40})`,
} as const

const icon = {
  default: `var(--icon-default, ${palette.silicon95})`,
  secondary: `var(--icon-secondary, ${palette.silicon60})`,
  action: `var(--icon-action, ${palette.lilac70})`,
  disabled: `var(--icon-disabled, ${palette.silicon40})`,
  highlight: `var(--icon-highlight, ${palette.lilac70})`,
  info: `var(--icon-info, ${palette.magnetic70})`,
  inverted: `var(--icon-inverted, ${palette.white})`,
  success: {
    large: `var(--icon-success-large, ${palette.mint50})`,
    small: `var(--icon-success-small, ${palette.mint70})`,
  },
  warning: {
    large: `var(--icon-warning-large, ${palette.turmeric50})`,
    small: `var(--icon-warning-small, ${palette.turmeric70})`,
  },
  error: {
    large: `var(--icon-error-large, ${palette.salmon40})`,
    small: `var(--icon-error-small, ${palette.salmon70})`,
  },
} as const

const action = {
  primary: {
    text: {
      default: `var(--action-primary-text-default, ${palette.white})`,
      hover: `var(--action-primary-text-hover, ${palette.white})`,
      selected: `var(--action-primary-text-selected, ${palette.white})`,
      disabled: `var(--action-primary-text-disabled, ${palette.white})`,
    },
    background: {
      default: `var(--action-primary-background-default, ${palette.lilac65})`,
      hover: `var(--action-primary-background-hover, ${palette.lilac70})`,
      selected: `var(--action-primary-background-selected, ${palette.lilac80})`,
      disabled: `var(--action-primary-background-disabled, ${palette.silicon30})`,
    },
    border: {
      default: `var(--action-primary-border-default, ${palette.lilac65})`,
      hover: `var(--action-primary-border-hover, ${palette.lilac70})`,
      selected: `var(--action-primary-border-selected, ${palette.lilac80})`,
      disabled: `var(--action-primary-border-disabled, ${palette.silicon30})`,
    },
  },
  secondary: {
    text: {
      default: `var(--action-secondary-text-default, ${palette.lilac80})`,
      hover: `var(--action-secondary-text-hover, ${palette.lilac80})`,
      selected: `var(--action-secondary-text-selected, ${palette.lilac80})`,
      disabled: `var(--action-secondary-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-secondary-background-default, ${palette.lilac10})`,
      hover: `var(--action-secondary-background-hover, ${palette.lilac20})`,
      selected: `var(--action-secondary-background-selected, ${palette.lilac30})`,
      disabled: `var(--action-secondary-background-disabled, ${palette.silicon05})`,
    },
    border: {
      default: `var(--action-secondary-border-default, ${palette.lilac20})`,
      hover: `var(--action-secondary-border-hover, ${palette.lilac30})`,
      selected: `var(--action-secondary-border-selected, ${palette.lilac40})`,
      disabled: `var(--action-secondary-border-disabled, ${palette.silicon10})`,
    },
  },
  destructive: {
    text: {
      default: `var(--action-destructive-text-default, ${palette.salmon80})`,
      hover: `var(--action-destructive-text-hover, ${palette.salmon80})`,
      selected: `var(--action-destructive-text-selected, ${palette.salmon80})`,
      disabled: `var(--action-destructive-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-destructive-background-default, ${palette.clear})`,
      hover: `var(--action-destructive-background-hover, ${palette.salmon00})`,
      selected: `var(--action-destructive-background-selected, ${palette.salmon05})`,
      disabled: `var(--action-destructive-background-disabled, ${palette.silicon05})`,
    },
    border: {
      default: `var(--action-destructive-border-default, ${`${palette.silicon95}33`})`,
      hover: `var(--action-destructive-border-hover, ${palette.salmon05})`,
      selected: `var(--action-destructive-border-selected, ${palette.salmon20})`,
      disabled: `var(--action-destructive-border-disabled, ${`${palette.silicon95}33`})`,
    },
  },
  destructiveSolid: {
    text: {
      default: `var(--action-destructive-solid-text-default, ${palette.white})`,
      hover: `var(--action-destructive-solid-text-hover, ${palette.white})`,
      selected: `var(--action-destructive-solid-text-selected, ${palette.white})`,
      disabled: `var(--action-destructive-solid-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-destructive-solid-background-default, ${palette.salmon70})`,
      hover: `var(--action-destructive-solid-background-hover, ${palette.salmon80})`,
      selected: `var(--action-destructive-solid-background-selected, ${palette.salmon90})`,
      disabled: `var(--action-destructive-solid-background-disabled, ${palette.silicon30})`,
    },
    border: {
      default: `var(--action-destructive-solid-border-default, ${palette.clear})`,
      hover: `var(--action-destructive-solid-border-hover, ${palette.clear})`,
      selected: `var(--action-destructive-solid-border-selected, ${palette.clear})`,
      disabled: `var(--action-destructive-solid-border-disabled, ${palette.clear})`,
    },
  },
  minor: {
    text: {
      default: `var(--action-minor-text-default, ${palette.silicon95})`,
      hover: `var(--action-minor-text-hover, ${palette.silicon95})`,
      selected: `var(--action-minor-text-selected, ${palette.silicon95})`,
      disabled: `var(--action-minor-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-minor-background-default, ${palette.clear})`,
      hover: `var(--action-minor-background-hover, ${palette.silicon15})`,
      selected: `var(--action-minor-background-selected, ${palette.silicon30})`,
      disabled: `var(--action-minor-background-disabled, ${palette.clear})`,
    },
    border: {
      default: `var(--action-minor-border-default, ${palette.silicon15})`,
      hover: `var(--action-minor-border-hover, ${palette.silicon30})`,
      selected: `var(--action-minor-border-selected, ${palette.silicon30})`,
      disabled: `var(--action-minor-border-disabled, ${palette.silicon15})`,
    },
  },
  inline: {
    text: {
      default: `var(--action-inline-text-default, ${palette.lilac70})`,
      hover: `var(--action-inline-text-hover, ${palette.lilac90})`,
      selected: `var(--action-inline-text-selected, ${palette.lilac90})`,
      disabled: `var(--action-inline-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-inline-background-default, ${palette.clear})`,
      hover: `var(--action-inline-background-hover, ${palette.clear})`,
      selected: `var(--action-inline-background-selected, ${palette.clear})`,
      disabled: `var(--action-inline-background-disabled, ${palette.clear})`,
    },
    border: {
      default: `var(--action-inline-border-default, ${palette.clear})`,
      hover: `var(--action-inline-border-hover, ${palette.clear})`,
      selected: `var(--action-inline-border-selected, ${palette.clear})`,
      disabled: `var(--action-inline-border-disabled, ${palette.clear})`,
    },
  },
  inlineDestructive: {
    text: {
      default: `var(--action-inline-destructive-text-default, ${palette.salmon80})`,
      hover: `var(--action-inline-destructive-text-hover, ${palette.salmon60})`,
      selected: `var(--action-inline-destructive-text-selected, ${palette.salmon40})`,
      disabled: `var(--action-inline-destructive-text-disabled, ${palette.silicon40})`,
    },
    background: {
      default: `var(--action-inline-destructive-background-default, ${palette.clear})`,
      hover: `var(--action-inline-destructive-background-hover, ${palette.clear})`,
      selected: `var(--action-inline-destructive-background-selected, ${palette.clear})`,
      disabled: `var(--action-inline-destructive-background-disabled, ${palette.clear})`,
    },
    border: {
      default: `var(--action-inline-destructive-border-default, ${palette.clear})`,
      hover: `var(--action-inline-destructive-border-hover, ${palette.clear})`,
      selected: `var(--action-inline-destructive-border-selected, ${palette.clear})`,
      disabled: `var(--action-inline-destructive-border-disabled, ${palette.clear})`,
    },
  },
} as const

const selector = {
  base: `var(--selector-base, ${palette.white})`,
  initial: {
    default: `var(--selector-initial-default, ${palette.silicon40})`,
    hover: `var(--selector-initial-hover, ${palette.silicon60})`,
    active: `var(--selector-initial-active, ${palette.silicon60})`,
  },
  selected: {
    default: `var(--selector-selected-default, ${palette.lilac65})`,
    hover: `var(--selector-selected-hover, ${palette.lilac70})`,
    active: `var(--selector-selected-active, ${palette.lilac80})`,
  },
  error: {
    default: `var(--selector-error-default, ${palette.salmon60})`,
    hover: `var(--selector-error-hover, ${palette.salmon70})`,
    active: `var(--selector-error-active, ${palette.salmon80})`,
  },
  disabled: {
    base: `var(--selector-disabled-base, ${palette.silicon00})`,
    accent: `var(--selector-disabled-accent, ${palette.silicon15})`,
  },
  disabledSelected: {
    base: `var(--selector-disabled-selected-base, ${palette.silicon10})`,
    accent: `var(--selector-disabled-selected-accent, ${palette.white})`,
  },
  focus: {
    border: `var(--selector-focus-border, ${palette.silicon95})`,
    background: `var(--selector-focus-background, ${palette.white})`,
  },
} as const

const brand = {
  magnetic: `var(--brand-magnetic, ${palette.magnetic80})`,
  salmon: `var(--brand-salmon, ${palette.salmon40})`,
  mint: `var(--brand-mint, ${palette.mint40})`,
  lilac: `var(--brand-lilac, ${palette.lilac50})`,
  silicon: `var(--brand-silicon, ${palette.silicon10})`,
} as const

export const colors = {
  text,
  background,
  border,
  icon,
  action,
  selector,
  brand,
} as const
